// src/contexts/AuthContext.js - COMPLETE VERSION WITH EMAIL AND USERNAME SUPPORT
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc,
  updateDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';

// Import your Firebase config
import { auth, db } from '../firebase';

// Import Firebase context if available (optional)
let useFirebase;
try {
  useFirebase = require('../FirebaseContext').useFirebase;
} catch (error) {
  console.warn('⚠️ FirebaseContext not found, using direct Firebase auth');
  useFirebase = () => ({ isConnected: !!(auth && db), user: null, loading: false });
}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [firebaseConnected, setFirebaseConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bikeDataLoaded, setBikeDataLoaded] = useState(false);
  const [documentFieldsReady, setDocumentFieldsReady] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [companyDataIsolated, setCompanyDataIsolated] = useState(false);
  const [tenantContext, setTenantContext] = useState(null);
  const [authMode, setAuthMode] = useState('hybrid'); // 'firebase', 'localStorage', 'hybrid'
  const [registrationInProgress, setRegistrationInProgress] = useState(false);

  const firebase = useFirebase ? useFirebase() : { isConnected: !!(auth && db), user: null, loading: false };

  // 🔍 FIND USER BY USERNAME (Helper function)
  const findUserByUsername = async (username) => {
    try {
      console.log('🔍 Looking up user by username:', username);
      
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('username', '==', username));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();
        console.log('✅ Found user by username:', {
          id: userDoc.id,
          username: userData.username,
          email: userData.email,
          isActive: userData.isActive
        });
        
        return {
          id: userDoc.id,
          ...userData
        };
      } else {
        console.log('❌ No user found with username:', username);
        return null;
      }
    } catch (error) {
      console.error('❌ Error looking up user by username:', error);
      return null;
    }
  };

  // 🔍 VALIDATE UNIQUE USERNAME (Helper function)
  const validateUniqueUsername = async (username) => {
    try {
      console.log('🔍 Checking if username is unique:', username);
      
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('username', '==', username));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        console.log('❌ Username already exists:', username);
        throw new Error(`Username "${username}" is already taken. Please choose a different username.`);
      }
      
      console.log('✅ Username is available:', username);
      return true;
    } catch (error) {
      if (error.message.includes('already taken')) {
        throw error;
      }
      console.warn('⚠️ Could not check username uniqueness:', error);
      // Continue anyway if we can't check (Firebase might be down)
      return true;
    }
  };

  // 👤 LOAD USER PROFILE FROM FIREBASE (with debugging)
  const loadUserProfile = async (firebaseUser) => {
    try {
      console.log('📋 Loading user profile from Firestore...');
      console.log('👤 Looking for user document with ID:', firebaseUser.uid);
      
      // Get user profile from Firestore
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('✅ User profile found:', {
          username: userData.username,
          email: userData.email,
          role: userData.role,
          companyId: userData.companyId,
          companyName: userData.companyName,
          isActive: userData.isActive
        });
        
        // Check if user is active
        if (!userData.isActive) {
          console.error('❌ User account is deactivated');
          await signOut(auth);
          throw new Error('Account is deactivated. Contact your administrator.');
        }
        
        // Load company data from Firestore
        if (userData.companyId) {
          console.log('🏢 Loading company data with ID:', userData.companyId);
          
          const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
          
          if (companyDoc.exists()) {
            const companyData = { id: userData.companyId, ...companyDoc.data() };
            console.log('✅ Company loaded:', {
              id: companyData.id,
              name: companyData.name,
              isActive: companyData.isActive,
              plan: companyData.plan
            });
            
            // Check if company is active
            if (!companyData.isActive) {
              console.error('❌ Company is deactivated');
              await signOut(auth);
              throw new Error('Company account is suspended. Contact support.');
            }
            
            const user = {
              id: firebaseUser.uid,
              email: firebaseUser.email,
              ...userData
            };
            
            setCurrentUser(user);
            setCurrentCompany(companyData);
            setAuthMode('firebase');
            
            setTenantContext({
              companyId: companyData.id,
              companyName: companyData.name,
              userId: firebaseUser.uid,
              userRole: userData.role,
              isolationActive: true,
              authMethod: 'firebase'
            });
            
            setCompanyDataIsolated(true);
            setBikeDataLoaded(true);
            setDocumentFieldsReady(true);
            
            // Save to localStorage for offline access
            localStorage.setItem('currentUser', JSON.stringify(user));
            localStorage.setItem('currentCompany', JSON.stringify(companyData));
            localStorage.setItem('authMode', 'firebase');
            
            console.log('🎉 LOGIN SUCCESSFUL - User and company loaded!');
            console.log('🌐 Firebase user data synced across browsers');
            console.log('🏢 Logged into company:', companyData.name);
            console.log('👤 User role:', userData.role);
            
            return;
          } else {
            console.error('❌ Company document not found with ID:', userData.companyId);
            console.log('🔍 Available companies in Firestore:');
            
            // Debug: List available companies
            try {
              const companiesSnapshot = await getDocs(collection(db, 'companies'));
              companiesSnapshot.forEach((doc) => {
                console.log('📋 Company:', doc.id, '-', doc.data().name);
              });
            } catch (err) {
              console.error('❌ Error listing companies:', err);
            }
            
            throw new Error('Company not found. Contact support.');
          }
        } else {
          console.error('❌ User has no company association');
          throw new Error('User account has no company association. Contact support.');
        }
      } else {
        console.error('❌ User profile not found in Firestore with ID:', firebaseUser.uid);
        console.log('🔍 Available users in Firestore:');
        
        // Debug: List available users
        try {
          const usersSnapshot = await getDocs(collection(db, 'users'));
          usersSnapshot.forEach((doc) => {
            const userData = doc.data();
            console.log('👤 User:', doc.id, '-', userData.email, '-', userData.username);
          });
        } catch (err) {
          console.error('❌ Error listing users:', err);
        }
        
        throw new Error('User profile not found. Please register again.');
      }
    } catch (error) {
      console.error('❌ Error loading user profile:', error);
      
      // Don't sign out during registration
      if (!registrationInProgress) {
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.error('❌ Error signing out:', signOutError);
        }
      }
      
      throw error;
    }
  };

  // 🔥 FIREBASE AUTH STATE LISTENER (with enhanced debugging)
  useEffect(() => {
    console.log('🔐 Setting up authentication system...', { firebaseAvailable: !!(auth && db) });
    
    if (auth && db) {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        try {
          if (firebaseUser) {
            console.log('🔥 AUTH STATE CHANGE - Firebase user detected:', firebaseUser.email);
            console.log('👤 User ID:', firebaseUser.uid);
            console.log('📧 User email:', firebaseUser.email);
            console.log('✅ Email verified:', firebaseUser.emailVerified);
            console.log('🕒 User created:', firebaseUser.metadata.creationTime);
            console.log('🕒 Last sign in:', firebaseUser.metadata.lastSignInTime);
            
            setFirebaseConnected(true);
            
            // If registration is in progress, wait for profile creation
            if (registrationInProgress) {
              console.log('⏳ Registration in progress, waiting for user profile creation...');
              setTimeout(async () => {
                try {
                  await loadUserProfile(firebaseUser);
                } catch (error) {
                  console.error('❌ Error loading user profile after registration:', error);
                  await initializeLocalAuthFallback();
                }
              }, 2000);
              return;
            }
            
            // Regular login - load user profile immediately
            console.log('📋 Loading user profile for login...');
            await loadUserProfile(firebaseUser);
            
          } else {
            console.log('🚪 No Firebase user - checking localStorage fallback...');
            await initializeLocalAuthFallback();
          }
        } catch (error) {
          console.error('❌ Firebase auth state change error:', error);
          await initializeLocalAuthFallback();
        } finally {
          if (!registrationInProgress) {
            setLoading(false);
            setAuthInitialized(true);
          }
        }
      });

      return () => unsubscribe();
    } else {
      // No Firebase available, use localStorage only
      console.log('📱 Firebase not available, using localStorage mode only');
      initializeLocalAuthFallback().then(() => {
        setLoading(false);
        setAuthInitialized(true);
      });
    }
  }, [registrationInProgress]);

  // 📱 LOCALSTORAGE FALLBACK INITIALIZATION
  const initializeLocalAuthFallback = async () => {
    try {
      console.log('🔄 Initializing localStorage authentication...');
      
      const savedUser = localStorage.getItem('currentUser');
      const savedCompany = localStorage.getItem('currentCompany');
      const savedAuthMode = localStorage.getItem('authMode');

      if (savedUser && savedCompany) {
        const user = JSON.parse(savedUser);
        const company = JSON.parse(savedCompany);
        
        // Validate user-company relationship
        if (user.companyId === company.id) {
          setCurrentUser(user);
          setCurrentCompany(company);
          setAuthMode(savedAuthMode || 'localStorage');
          setTenantContext({
            companyId: company.id,
            companyName: company.name,
            userId: user.id,
            userRole: user.role,
            isolationActive: true,
            authMethod: savedAuthMode || 'localStorage'
          });
          setCompanyDataIsolated(true);
          setBikeDataLoaded(true);
          setDocumentFieldsReady(true);
          console.log('✅ Multi-tenant user restored from localStorage:', user.username || user.email, 'for company:', company.name);
          
          if (savedAuthMode !== 'firebase') {
            console.log('⚠️ Using localStorage mode - data won\'t sync across browsers');
          }
        } else {
          console.warn('⚠️ User-company relationship invalid, clearing session');
          localStorage.removeItem('currentUser');
          localStorage.removeItem('currentCompany');
          localStorage.removeItem('authMode');
        }
      }
    } catch (error) {
      console.error('❌ Error initializing localStorage fallback:', error);
    }
  };

  // 🔑 ENHANCED MULTI-TENANT LOGIN WITH EMAIL AND USERNAME SUPPORT
  const login = async (emailOrUsername, password) => {
    try {
      console.log('🔑 LOGIN DEBUG - Starting login attempt...', {
        emailOrUsername: emailOrUsername,
        isEmail: emailOrUsername.includes('@'),
        hasFirebaseAuth: !!(auth),
        hasFirebaseDb: !!(db),
        passwordLength: password.length
      });
      
      // Try Firebase authentication (supports both email and username)
      if (auth && db) {
        let loginEmail = emailOrUsername;
        let userProfile = null;
        
        try {
          // If it's not an email format, try to find the user by username first
          if (!emailOrUsername.includes('@')) {
            console.log('👤 Input is username, looking up email address...');
            userProfile = await findUserByUsername(emailOrUsername);
            
            if (userProfile && userProfile.email) {
              loginEmail = userProfile.email;
              console.log('✅ Found email for username:', userProfile.email);
            } else {
              console.log('❌ Username not found in Firebase, trying localStorage...');
              return await loginWithLocalStorage(emailOrUsername, password);
            }
          }
          
          console.log('🔥 Attempting Firebase login...');
          console.log('📧 Login email:', loginEmail);
          console.log('👤 Login method:', emailOrUsername.includes('@') ? 'email' : 'username->email');
          
          const userCredential = await signInWithEmailAndPassword(auth, loginEmail, password);
          console.log('✅ Firebase authentication successful');
          console.log('👤 Firebase user ID:', userCredential.user.uid);
          console.log('📧 Firebase user email:', userCredential.user.email);
          
          setFirebaseConnected(true);
          
          // The onAuthStateChanged listener will handle loading the user profile
          console.log('⏳ Waiting for auth state change to load user profile...');
          
          return { 
            success: true, 
            method: 'firebase', 
            userId: userCredential.user.uid,
            loginType: emailOrUsername.includes('@') ? 'email' : 'username'
          };
          
        } catch (firebaseError) {
          console.error('❌ Firebase login failed:', firebaseError);
          console.error('🔍 Error code:', firebaseError.code);
          console.error('🔍 Error message:', firebaseError.message);
          
          // Provide specific error messages
          if (firebaseError.code === 'auth/user-not-found') {
            // If we tried username->email lookup but user not found in Firebase Auth
            if (!emailOrUsername.includes('@')) {
              console.log('🔄 Username found in Firestore but not in Firebase Auth, trying localStorage...');
              return await loginWithLocalStorage(emailOrUsername, password);
            }
            throw new Error('No account found with this email address.');
          } else if (firebaseError.code === 'auth/wrong-password') {
            throw new Error('Incorrect password.');
          } else if (firebaseError.code === 'auth/invalid-email') {
            throw new Error('Invalid email address format.');
          } else if (firebaseError.code === 'auth/user-disabled') {
            throw new Error('This account has been disabled.');
          } else if (firebaseError.code === 'auth/too-many-requests') {
            throw new Error('Too many failed login attempts. Please try again later.');
          } else {
            // For other Firebase errors, try localStorage fallback
            console.log('🔄 Firebase error, trying localStorage fallback...');
            return await loginWithLocalStorage(emailOrUsername, password);
          }
        }
      }
      
      // Fallback to localStorage authentication
      console.log('📱 Firebase not available, attempting localStorage login...');
      return await loginWithLocalStorage(emailOrUsername, password);
      
    } catch (error) {
      console.error('❌ Multi-tenant login failed:', error.message);
      throw error;
    }
  };

  // 📱 LOCALSTORAGE LOGIN (Fallback/Demo mode)
  const loginWithLocalStorage = async (usernameOrEmail, password) => {
    // For demo purposes, accept admin/admin123
    if (usernameOrEmail === 'admin' && password === 'admin123') {
      return createTemporaryAdmin();
    }
    
    // Try to find user in localStorage (multi-tenant users)
    const savedUsers = localStorage.getItem('bikeManagerUsers');
    if (savedUsers) {
      const allUsers = JSON.parse(savedUsers);
      const user = allUsers.find(u => 
        (u.username === usernameOrEmail || u.email === usernameOrEmail) && u.password === password
      );
      
      if (user) {
        // Validate user status
        if (!user.isActive) {
          throw new Error('Account is deactivated. Contact your administrator.');
        }

        if (!user.companyId) {
          throw new Error('User account is missing company association. Contact support.');
        }

        // Find and validate user's company
        const savedCompanies = localStorage.getItem('bikeManagerCompanies');
        if (savedCompanies) {
          const allCompanies = JSON.parse(savedCompanies);
          const company = allCompanies.find(c => c.id === user.companyId);

          if (!company) {
            throw new Error('Company not found. Contact support.');
          }

          if (!company.isActive) {
            throw new Error('Company account is suspended. Contact support.');
          }

          // Set user and company context
          setCurrentUser(user);
          setCurrentCompany(company);
          setAuthMode('localStorage');
          setTenantContext({
            companyId: company.id,
            companyName: company.name,
            userId: user.id,
            userRole: user.role,
            isolationActive: true,
            authMethod: 'localStorage'
          });
          setCompanyDataIsolated(true);
          setBikeDataLoaded(true);
          setDocumentFieldsReady(true);

          // Save to localStorage
          localStorage.setItem('currentUser', JSON.stringify(user));
          localStorage.setItem('currentCompany', JSON.stringify(company));
          localStorage.setItem('authMode', 'localStorage');

          // Update last login
          user.lastLogin = new Date().toISOString();
          const updatedUsers = allUsers.map(u => u.id === user.id ? user : u);
          localStorage.setItem('bikeManagerUsers', JSON.stringify(updatedUsers));

          console.log('✅ localStorage login successful');
          console.log('👤 User:', usernameOrEmail, '| 🏢 Company:', company.name, '| 📱 Local mode');
          console.log('⚠️ Data will not sync across browsers in localStorage mode');
          
          return { user, company, method: 'localStorage' };
        } else {
          throw new Error('No companies found in system. Contact administrator.');
        }
      }
    }
    
    throw new Error('Invalid username/email or password');
  };

  // 🏢 REGISTER COMPANY WITH FIREBASE (ENHANCED WITH USERNAME SUPPORT)
  const registerCompany = async (companyData, adminData) => {
    try {
      console.log('🏢 REGISTRATION DEBUG - Starting registration...', {
        companyName: companyData.companyName,
        adminEmail: adminData.adminEmail,
        adminUsername: adminData.adminUsername,
        hasFirebaseAuth: !!(auth),
        hasFirebaseDb: !!(db),
        isEmailValid: adminData.adminEmail?.includes('@')
      });

      // Check Firebase availability
      if (!auth || !db) {
        console.warn('❌ Firebase not available - auth:', !!auth, 'db:', !!db);
        console.log('📱 Falling back to localStorage registration...');
        return await registerCompanyWithLocalStorage(companyData, adminData);
      }

      if (!adminData.adminEmail || !adminData.adminEmail.includes('@')) {
        console.warn('❌ Invalid email provided:', adminData.adminEmail);
        console.log('📱 Falling back to localStorage registration...');
        return await registerCompanyWithLocalStorage(companyData, adminData);
      }

      if (!adminData.adminUsername || adminData.adminUsername.trim().length < 3) {
        throw new Error('Username must be at least 3 characters long.');
      }

      try {
        console.log('🔥 Attempting Firebase registration...');
        console.log('📧 Creating user with email:', adminData.adminEmail);
        console.log('👤 Username will be:', adminData.adminUsername);
        
        // Check if username is unique BEFORE creating Firebase user
        await validateUniqueUsername(adminData.adminUsername.trim());
        
        // Set registration in progress flag
        setRegistrationInProgress(true);
        
        // Create Firebase user account
        const userCredential = await createUserWithEmailAndPassword(
          auth, 
          adminData.adminEmail, 
          adminData.adminPassword
        );
        
        console.log('✅ Firebase user created:', userCredential.user.uid);
        
        const firebaseUser = userCredential.user;
        const companyId = `company_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        console.log('🏢 Creating company document with ID:', companyId);
        
        // Create company document in Firestore
        const company = {
          name: companyData.companyName,
          industry: companyData.industry || 'bike-rental',
          phoneNumber: companyData.phoneNumber || '',
          address: companyData.address || '',
          isActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          plan: 'trial',
          maxUsers: 10,
          features: ['basic', 'documentFields', 'bikeManagement', 'multiTenant', 'crossBrowserSync'],
          ownerId: firebaseUser.uid,
          settings: {
            currency: 'USD',
            timezone: 'UTC',
            dataRetention: '1year'
          }
        };
        
        console.log('💾 Saving company to Firestore...');
        await setDoc(doc(db, 'companies', companyId), company);
        console.log('✅ Company document saved');
        
        // Create admin user profile in Firestore with proper username indexing
        const adminUser = {
          username: adminData.adminUsername.trim(),
          email: adminData.adminEmail,
          role: 'admin',
          permissions: [
            'dashboard', 
            'bikeManagement', 
            'bikeAnalytics', 
            'invoice', 
            'users', 
            'bookings', 
            'reservations', 
            'financeManager', 
            'quotationGenerator', 
            'apiConfig',
            'documentFields',
            'companySettings'
          ],
          isActive: true,
          companyId: companyId,
          companyName: company.name,
          createdAt: serverTimestamp(),
          createdBy: 'system',
          isCompanyAdmin: true,
          profile: {
            firstName: adminData.firstName || '',
            lastName: adminData.lastName || '',
            phone: adminData.phoneNumber || ''
          },
          // Add search fields for better querying
          searchFields: {
            username: adminData.adminUsername.trim().toLowerCase(),
            email: adminData.adminEmail.toLowerCase()
          }
        };
        
        console.log('👤 Saving user profile to Firestore...');
        await setDoc(doc(db, 'users', firebaseUser.uid), adminUser);
        console.log('✅ User profile saved with username:', adminData.adminUsername);
        
        // Clear registration in progress flag
        setRegistrationInProgress(false);
        
        // Now manually trigger the profile loading
        console.log('🔄 Loading user profile after registration...');
        try {
          await loadUserProfile(firebaseUser);
          setLoading(false);
          setAuthInitialized(true);
        } catch (profileError) {
          console.error('❌ Error loading profile after registration:', profileError);
        }
        
        console.log('🎉 Firebase registration completed successfully!');
        console.log('📊 Check Firebase Console:');
        console.log('   - Authentication > Users (should see:', adminData.adminEmail, ')');
        console.log('   - Firestore Database > companies >', companyId);
        console.log('   - Firestore Database > users >', firebaseUser.uid);
        console.log('🔐 Login Options:');
        console.log('   - Email:', adminData.adminEmail);
        console.log('   - Username:', adminData.adminUsername);
        
        return { success: true, user: adminUser, company: { id: companyId, ...company }, method: 'firebase' };
        
      } catch (firebaseError) {
        console.error('❌ Firebase registration failed:', firebaseError);
        console.error('🔍 Error code:', firebaseError.code);
        console.error('🔍 Error message:', firebaseError.message);
        
        // Clear registration in progress flag on error
        setRegistrationInProgress(false);
        
        if (firebaseError.code === 'auth/email-already-in-use') {
          throw new Error('This email is already registered. Try logging in instead.');
        } else if (firebaseError.code === 'auth/weak-password') {
          throw new Error('Password is too weak. Please use at least 6 characters.');
        } else if (firebaseError.code === 'auth/invalid-email') {
          throw new Error('Invalid email address format.');
        } else if (firebaseError.message && firebaseError.message.includes('already taken')) {
          // Username uniqueness error
          throw firebaseError;
        } else {
          console.log('🔄 Falling back to localStorage due to Firebase error...');
          return await registerCompanyWithLocalStorage(companyData, adminData);
        }
      }
      
    } catch (error) {
      console.error('❌ Registration failed:', error);
      setRegistrationInProgress(false);
      throw error;
    }
  };

  // 📱 LOCALSTORAGE COMPANY REGISTRATION (Fallback)
  const registerCompanyWithLocalStorage = async (companyData, adminData) => {
    const companyId = 'company-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    const company = {
      id: companyId,
      name: companyData.companyName,
      industry: companyData.industry || 'bike-rental',
      phoneNumber: companyData.phoneNumber || '',
      address: companyData.address || '',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      plan: 'trial',
      maxUsers: 10,
      features: ['basic', 'documentFields', 'bikeManagement', 'multiTenant']
    };

    const adminUser = {
      id: 'user-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      username: adminData.adminUsername,
      email: adminData.adminEmail,
      password: adminData.adminPassword, // In production, this would be hashed
      role: 'admin',
      permissions: [
        'dashboard', 
        'bikeManagement', 
        'bikeAnalytics', 
        'invoice', 
        'users', 
        'bookings', 
        'reservations', 
        'financeManager', 
        'quotationGenerator', 
        'apiConfig',
        'documentFields',
        'companySettings'
      ],
      isActive: true,
      companyId: companyId,
      companyName: company.name,
      createdAt: new Date().toISOString(),
      createdBy: 'system',
      isCompanyAdmin: true,
      profile: {
        firstName: adminData.firstName || '',
        lastName: adminData.lastName || '',
        phone: adminData.phone || ''
      }
    };

    // Save company
    const savedCompanies = localStorage.getItem('bikeManagerCompanies');
    const allCompanies = savedCompanies ? JSON.parse(savedCompanies) : [];
    allCompanies.push(company);
    localStorage.setItem('bikeManagerCompanies', JSON.stringify(allCompanies));

    // Save admin user
    const savedUsers = localStorage.getItem('bikeManagerUsers');
    const allUsers = savedUsers ? JSON.parse(savedUsers) : [];
    allUsers.push(adminUser);
    localStorage.setItem('bikeManagerUsers', JSON.stringify(allUsers));

    // Auto-login the new admin
    setCurrentUser(adminUser);
    setCurrentCompany(company);
    setAuthMode('localStorage');
    setTenantContext({
      companyId: company.id,
      companyName: company.name,
      userId: adminUser.id,
      userRole: adminUser.role,
      isolationActive: true,
      authMethod: 'localStorage'
    });
    setCompanyDataIsolated(true);
    setBikeDataLoaded(true);
    setDocumentFieldsReady(true);

    localStorage.setItem('currentUser', JSON.stringify(adminUser));
    localStorage.setItem('currentCompany', JSON.stringify(company));
    localStorage.setItem('authMode', 'localStorage');

    console.log('✅ Company registered with localStorage - local browser only');
    console.log('⚠️ To sync across browsers, use Firebase registration instead');

    return { success: true, user: adminUser, company, method: 'localStorage' };
  };

  // 🏢 CREATE DEMO MULTI-TENANT ADMIN
  const createTemporaryAdmin = () => {
    const companyId = 'demo-company-' + Date.now();
    const tempCompany = {
      id: companyId,
      name: 'Demo Bike Rentals',
      industry: 'bike-rental',
      isActive: true,
      plan: 'trial',
      features: ['basic', 'documentFields', 'bikeManagement', 'multiTenant', 'realTimeSync'],
      settings: {
        currency: 'USD',
        timezone: 'UTC',
        dataRetention: '1year'
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const tempAdmin = {
      id: 'temp-admin-' + Date.now(),
      username: 'admin',
      password: 'admin123',
      email: 'admin@bikerentals.com',
      role: 'admin',
      permissions: [
        'dashboard', 
        'bikeManagement', 
        'bikeAnalytics', 
        'invoice', 
        'users', 
        'bookings', 
        'reservations', 
        'financeManager', 
        'quotationGenerator', 
        'apiConfig',
        'documentFields',
        'companySettings'
      ],
      isActive: true,
      companyId: companyId,
      companyName: tempCompany.name,
      profile: {
        firstName: 'Demo',
        lastName: 'Admin',
        phone: '+1234567890'
      },
      createdAt: new Date().toISOString(),
      createdBy: 'system',
      createdByUsername: 'system',
      lastLogin: new Date().toISOString()
    };

    // Save to localStorage for demo
    localStorage.setItem('currentUser', JSON.stringify(tempAdmin));
    localStorage.setItem('currentCompany', JSON.stringify(tempCompany));
    localStorage.setItem('authMode', 'demo');
    
    // Save to multi-tenant lists
    const savedCompanies = localStorage.getItem('bikeManagerCompanies') || '[]';
    const companies = JSON.parse(savedCompanies);
    if (!companies.find(c => c.id === companyId)) {
      companies.push(tempCompany);
      localStorage.setItem('bikeManagerCompanies', JSON.stringify(companies));
    }

    const savedUsers = localStorage.getItem('bikeManagerUsers') || '[]';
    const users = JSON.parse(savedUsers);
    if (!users.find(u => u.id === tempAdmin.id)) {
      users.push(tempAdmin);
      localStorage.setItem('bikeManagerUsers', JSON.stringify(users));
    }

    setCurrentUser(tempAdmin);
    setCurrentCompany(tempCompany);
    setAuthMode('demo');
    setTenantContext({
      companyId: tempCompany.id,
      companyName: tempCompany.name,
      userId: tempAdmin.id,
      userRole: tempAdmin.role,
      isolationActive: true,
      authMethod: 'demo'
    });
    setCompanyDataIsolated(true);
    setBikeDataLoaded(true);
    setDocumentFieldsReady(true);
    
    console.log('✅ Multi-tenant demo admin created:', tempAdmin.username, 'for company:', tempCompany.name);
    console.log('📱 Demo mode - data local to this browser only');
    
    return { user: tempAdmin, company: tempCompany, method: 'demo' };
  };

  // 🚪 ENHANCED LOGOUT WITH TENANT CLEANUP
  const logout = async () => {
    console.log('🚪 Logging out multi-tenant user:', currentUser?.username || currentUser?.email, 'from company:', currentCompany?.name, '| Mode:', authMode);
    
    // Clear all state
    setCurrentUser(null);
    setCurrentCompany(null);
    setTenantContext(null);
    setBikeDataLoaded(false);
    setDocumentFieldsReady(false);
    setCompanyDataIsolated(false);
    setAuthInitialized(false);
    setLoading(true);
    setRegistrationInProgress(false);
    
    // Clear localStorage
    localStorage.removeItem('currentUser');
    localStorage.removeItem('currentCompany');
    localStorage.removeItem('authMode');
    
    // Sign out from Firebase if using Firebase auth
    if (authMode === 'firebase' && auth) {
      try {
        await signOut(auth);
        console.log('🔥 Firebase logout completed');
      } catch (error) {
        console.warn('⚠️ Firebase logout error:', error);
      }
    }
    
    setAuthMode('hybrid');
    setFirebaseConnected(false);
    setLoading(false);
    setAuthInitialized(true);
    
    console.log('✅ Multi-tenant logout completed - all company data cleared');
  };

  // 🔄 REFRESH USER DATA
  const refreshUser = async () => {
    if (!currentUser) return;
    
    console.log('🔄 Refreshing user data for multi-tenant context...');
    
    try {
      if (authMode === 'firebase' && currentUser.id && db) {
        // Refresh from Firebase
        const userDoc = await getDoc(doc(db, 'users', currentUser.id));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const updatedUser = { ...currentUser, ...userData };
          setCurrentUser(updatedUser);
          localStorage.setItem('currentUser', JSON.stringify(updatedUser));
          console.log('✅ User data refreshed from Firebase');
        }
      } else {
        // Refresh from localStorage
        const savedUsers = localStorage.getItem('bikeManagerUsers');
        if (savedUsers) {
          const allUsers = JSON.parse(savedUsers);
          const updatedUser = allUsers.find(u => u.id === currentUser.id);
          
          if (updatedUser && updatedUser.companyId === currentCompany?.id) {
            setCurrentUser(updatedUser);
            localStorage.setItem('currentUser', JSON.stringify(updatedUser));
            console.log('✅ User data refreshed from localStorage');
          }
        }
      }
    } catch (error) {
      console.error('❌ Error refreshing user data:', error);
    }
  };

  // 📧 PASSWORD RESET (Firebase only)
  const resetPassword = async (email) => {
    if (!auth || !db) {
      throw new Error('Password reset requires Firebase connection');
    }

    try {
      await sendPasswordResetEmail(auth, email);
      console.log('✅ Password reset email sent');
    } catch (error) {
      console.error('❌ Password reset failed:', error);
      throw error;
    }
  };

  // 🛠️ UTILITY FUNCTIONS
  const canManageUsers = () => {
    if (!currentUser || !currentCompany) return false;
    return (currentUser.role === 'admin' || currentUser.role === 'manager') && 
           currentUser.permissions?.includes('users');
  };

  const isCompanyAdmin = () => {
    if (!currentUser || !currentCompany) return false;
    return currentUser.role === 'admin' && currentUser.companyId === currentCompany.id;
  };

  const getCompanyContext = () => {
    const companyId = currentUser?.companyId || null;
    if (!companyId) {
      console.warn('⚠️ getCompanyContext: No company context available');
    }
    return companyId;
  };

  const hasPermission = (pageId) => {
    if (!currentUser || !currentCompany) {
      console.warn(`⚠️ hasPermission(${pageId}): No user or company context`);
      return false;
    }
    
    if (currentUser.companyId !== currentCompany.id) {
      console.error(`🚫 hasPermission(${pageId}): Company mismatch - security violation`);
      return false;
    }
    
    if (currentUser.role === 'admin') {
      return true;
    }
    
    return currentUser.permissions?.includes(pageId) || false;
  };

  const validateCompanyAccess = (dataCompanyId) => {
    if (!currentUser || !currentCompany) return false;
    if (!dataCompanyId) return false;
    return currentCompany.id === dataCompanyId;
  };

  const getTenantStats = () => {
    if (!currentUser || !currentCompany) return null;
    
    return {
      companyId: currentCompany.id,
      companyName: currentCompany.name,
      userId: currentUser.id,
      userRole: currentUser.role,
      dataIsolated: companyDataIsolated,
      authMode: authMode,
      crossBrowserSync: authMode === 'firebase',
      firebaseAvailable: !!(auth && db),
      servicesReady: {
        bikeData: bikeDataLoaded,
        documentFields: documentFieldsReady,
        firebase: firebaseConnected
      },
      tenantContext: tenantContext
    };
  };

  const isMultiTenantReady = () => {
    return !!(currentUser && currentCompany && authInitialized && companyDataIsolated);
  };

  const value = {
    // Core State
    currentUser,
    currentCompany,
    firebaseConnected,
    loading,
    bikeDataLoaded,
    documentFieldsReady,
    authInitialized,
    authMode,
    
    // Multi-Tenant State
    companyDataIsolated,
    tenantContext,

    // Authentication Methods
    login,
    logout,
    refreshUser,
    registerCompany,
    resetPassword,
    
    // Permission & Access Control
    canManageUsers,
    isCompanyAdmin,
    hasPermission,
    validateCompanyAccess,
    
    // Multi-Tenant Context
    getCompanyContext,
    getTenantStats,
    isMultiTenantReady,
    
    // Admin Creation (Development/Demo)
    createTemporaryAdmin,
    
    // Username helpers (for other components)
    findUserByUsername,
    validateUniqueUsername
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};